import React, { useCallback, useMemo, useState } from 'react';

// Styles
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Grid, Tab, Skeleton, useMediaQuery, useTheme } from '@mui/material';

// Hooks
import useReaderDetail from '../../../hooks/useReaderDetail';

// Constants
import { AppPermissions } from '../../../constants/Permissions';

// Types
import { ResourceType } from '../../../types/ResourceType';

// Common
import { CustomImageList } from '../../common/CustomImageList';

// Components
import AuthService from '../../../services/auth.service';
import ReaderReports from '../readerReport/readerReportList';
import NearFacilities from '../facilities/nearFacilities';
import CommentList from '../comments/commentList';
import ReaderConfiguration from './readerConfiguration';
import ReaderStage from './readerStage';
import ReaderLogs from './readerLogs';
import NearReaders from './nearReaders';
import Map from '../../maps/Map';
import Shimmer from '../../common/Shimmer/Shimmer';

// Styles
import '../../../styles/muiTabs.scss';

const ReaderDetail = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeRightTab, setActiveRightTab] = useState('facilities');
  const [activeBottomTab, setBottomActiveTab] = useState('1');
  const [hoveredImei, setHoveredImei] = useState<string | null>(null);
  const [nearReaderOpsStatus, setNearReaderOpsStatus] = useState<string | null>('all');
  const [nearReaderIntendedUse, setNearReaderIntendedUse] = useState<string | null>('all');

  const { readerState, settingsState, centerMap, positions, shouldFetch, handleConfigUpdated, handleMoveCenter, onReaderReportClick } =
    useReaderDetail();

  const handleRightTabchange = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setActiveRightTab(newValue);
  }, []);

  const handleBottomTabchange = useCallback((event: React.SyntheticEvent, newValue: string) => setBottomActiveTab(newValue), []);

  const handleNearReaderFiltersChange = useCallback((opsStatus: string | null, intendedUse: string | null) => {
    setNearReaderOpsStatus(opsStatus);
    setNearReaderIntendedUse(intendedUse);
  }, []);

  const renderNearFacilities = useMemo(() => {
    if (!readerState?.reader || !shouldFetch) return null;
    return <NearFacilities reader={readerState.reader} shouldFetch={shouldFetch} onSelected={handleMoveCenter} />;
  }, [readerState?.reader, shouldFetch]);

  return (
    <>
      {readerState.reader && <ReaderStage reader={readerState.reader} settings={settingsState} />}
      <Grid container spacing={0}>
        <Grid item xs={12} sm={12} lg={6}>
          {readerState.reader && centerMap ? (
            <Map
              center={centerMap}
              coordinates={positions}
              aciveToggleTab={['facilities', 'calibration']}
              showToggleButtons={true}
              // showToggleButtonAirportGeofences={true}
              lockedLocation={readerState?.reader?.lockedLocation}
              showSatelliteMap={true}
              hoveredImei={hoveredImei}
              onNearReaderHover={setHoveredImei}
              nearReaderOpsStatus={nearReaderOpsStatus}
              nearReaderIntendedUse={nearReaderIntendedUse}
            />
          ) : (
            <Skeleton animation='wave' variant='circular' width={40} height={40}></Skeleton>
          )}
        </Grid>
        <Grid item xs={12} sm={12} lg={6} style={{ border: '2px solid #e5e7eb' }}>
          <TabContext value={activeRightTab}>
            <div className='app-tabs'>
              <TabList onChange={handleRightTabchange} aria-label='lab API tabs example'>
                {AuthService.hasPermission(AppPermissions.Facility.Read) && (
                  <Tab label='Facilities' value='facilities' sx={{ textTransform: 'none' }} />
                )}
                <Tab label='Near Readers' value='nearreader' sx={{ textTransform: 'none' }} />
                <Tab label='Configuration' value='configurations' sx={{ textTransform: 'none' }} />
                <Tab label='Images' value='images' sx={{ textTransform: 'none' }} />
              </TabList>
            </div>
            {AuthService.hasPermission(AppPermissions.Facility.Read) && (
              <TabPanel
                value='facilities'
                style={{
                  height: '630px',
                  overflowY: 'auto',
                  paddingTop: 10,
                  paddingLeft: 10,
                }}
              >
                {renderNearFacilities}
              </TabPanel>
            )}
            <TabPanel
              value='nearreader'
              style={{
                height: '630px',
                overflowY: 'auto',
                paddingTop: 10,
                paddingLeft: 2,
              }}
            >
              {readerState.reader ? (
                <NearReaders
                  reader={readerState?.reader}
                  shouldFetch={shouldFetch}
                  hoveredImei={hoveredImei}
                  onNearReaderHover={setHoveredImei}
                  onFiltersChange={handleNearReaderFiltersChange}
                />
              ) : (
                <Skeleton animation='wave' variant='circular' width={40} height={40}></Skeleton>
              )}
            </TabPanel>
            <TabPanel value='configurations' style={{ height: '630px', paddingTop: 10, paddingLeft: 2, display: 'contents' }}>
              {readerState?.reader && <ReaderConfiguration reader={readerState?.reader} onUpdated={handleConfigUpdated} />}
            </TabPanel>
            <TabPanel value='images' style={{ height: '630px', paddingTop: 10, paddingLeft: 2 }}>
              {readerState?.reader && (
                <CustomImageList
                  subjectId={readerState?.reader.id}
                  subjectIdentifier={readerState?.reader.imei}
                  imageCols={isMobile ? 2 : 6}
                  imageHeight={160}
                  resourceType={ResourceType.Reader}
                />
              )}
            </TabPanel>
          </TabContext>
        </Grid>
        {/* device */}
        <Grid item xs={12}>
          <TabContext value={activeBottomTab}>
            <div className='app-tabs'>
              <TabList onChange={handleBottomTabchange} aria-label='lab API tabs example'>
                {AuthService.hasPermission(AppPermissions.ReaderReport.Read) && (
                  <Tab label='Reader Report' value='1' sx={{ textTransform: 'none' }} />
                )}
                {AuthService.hasPermission(AppPermissions.Log.Reader.Read) && <Tab label='Change Log' value='2' sx={{ textTransform: 'none' }} />}
                {AuthService.hasPermission(AppPermissions.Comment.Read) && <Tab label='Comments' value='3' sx={{ textTransform: 'none' }} />}
              </TabList>
            </div>
            {AuthService.hasPermission(AppPermissions.ReaderReport.Read) && (
              <TabPanel value='1' style={{ height: '100vh', display: 'contents' }}>
                {readerState?.reader ? (
                  <ReaderReports reader={readerState.reader} shouldFetch={shouldFetch} onRowClick={onReaderReportClick} />
                ) : (
                  <Shimmer variant='rectangular' width='100%' height={35} count={12} spacing={1} />
                )}
              </TabPanel>
            )}

            {AuthService.hasPermission(AppPermissions.Log.Reader.Read) && (
              <TabPanel value='2' style={{ height: '100vh', display: 'contents' }}>
                {readerState?.reader ? (
                  <ReaderLogs reader={readerState.reader} />
                ) : (
                  <Shimmer variant='rectangular' width='100%' height={35} count={12} spacing={1} />
                )}
              </TabPanel>
            )}

            {AuthService.hasPermission(AppPermissions.Comment.Read) && (
              <TabPanel value='3' style={{ height: '100vh', display: 'contents' }}>
                {readerState?.reader ? (
                  <CommentList reader={readerState.reader} />
                ) : (
                  <Shimmer variant='rectangular' width='100%' height={35} count={12} spacing={1} />
                )}
              </TabPanel>
            )}
          </TabContext>
        </Grid>
      </Grid>
    </>
  );
};

export default ReaderDetail;
